import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PageFooter from '../components/PageFooter';
import ProductCartCounter from '../components/ProductCartCounter';
import RecommendedProductCard from '../components/RecommendedProductCard';
import CheckoutModal from '../components/CheckoutModal';
import { useSkinSession } from '../context/SkinSessionContext';
import { SESSION_APP_DATA_BASE } from '../config/sessionAppData';
import { SEGMENTATION_API_BASE, resolveSegmentationProductActions, isValidProductUrl } from '../utils/segmentation';
import { getSessionIdFromUrl, getSessionId, pathWithSessionId } from '../utils/sessionManager';
import { saveSkinFlowState, getSkinFlowState } from '../utils/skinFlowState';
import { getApiBaseUrl } from '../utils/apiConfig';
import { sanitizeHtml, stripHtml } from '../utils/sanitizeHtml';
import { fetchWithTimeout } from '../utils/fetchWithTimeout';
import { flattenRecommendedProducts, dedupeProductsById } from '../utils/normalizeRecommendedProducts';
import { buildRecommendableConditions, buildRecommendationPayloadScores } from '../utils/recommendationSeverity';
import { buildEnvironmentalAnalyticsFields, recordSkinFlowStage, saveSkinAnalyticsEvent } from '../utils/dataSaver';
import '../styles/PageBackground.css';
import './ProductRecommendationsPage.css';


const formatProductPrice = (value) => {
  if (value == null || value === '') return '₹0.00';
  const normalized = Number(String(value).replace(/[^0-9.-]/g, ''));
  if (Number.isNaN(normalized)) return '₹0.00';
  return `₹${normalized.toFixed(2)}`;
};

const parseOptionalAge = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  if (!normalized) return null;
  const age = Number(normalized);
  if (!Number.isFinite(age) || age <= 0) return null;
  return age;
};

const ProductRecommendationsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, sessionLandingPath } = useSkinSession();

  const sessionIdFromState = location.state?.sessionId || null;
  const sessionIdFromUrl = getSessionIdFromUrl(location);
  const sessionIdFromStorage = getSessionId();
  const sessionId = sessionIdFromState || sessionIdFromUrl || sessionIdFromStorage || null;

  const effectiveState = useMemo(() => {
    if (location.state && (location.state.skinResults || location.state.routineData || location.state.recommendations)) return location.state;
    if (!sessionId) return null;
    return getSkinFlowState(sessionId);
  }, [location.state, sessionId]);

  const userDataFromState = effectiveState?.userData || location.state?.userData || null;
  const capturedImageFromState = effectiveState?.capturedImage || location.state?.capturedImage || null;
  const skinResultsFromState = effectiveState?.skinResults || location.state?.skinResults || null;
  const skinGoalDataFromState = effectiveState?.skinGoalData || location.state?.skinGoalData || null;
  const routineDataFromState = effectiveState?.routineData || location.state?.routineData || null;
  const aqiDataFromState = effectiveState?.aqiData ?? location.state?.aqiData ?? null;
  const cartItemsFromState = effectiveState?.cartItems ?? location.state?.cartItems ?? null;
  const updatedCartFromCheckout = location.state?.updatedCartFromCheckout || false;
  const [cartItems, setCartItems] = useState(cartItemsFromState || location.state?.cartItems || {}); // Store cart items: { productId: { product, quantity } });

  // Persist full state (including cartItems) so it survives refresh
  useEffect(() => {
    if (!sessionId) return;
    const current = getSkinFlowState(sessionId) || {};
    const fromLocation = location.state && typeof location.state === 'object' ? location.state : {};
    const merged = {
      ...current,
      ...fromLocation,
      sessionId,
      cartItems
    };
    saveSkinFlowState(sessionId, merged);
  }, [sessionId, cartItems, location.state]);
  const [recommendations, setRecommendations] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // No morning/evening tabs anymore — single flat list
  const [uspPopup, setUspPopup] = useState({ open: false, text: '', heading: '', position: { top: 0, left: 0 } });
  const [isCheckoutModalOpen, setIsCheckoutModalOpen] = useState(false);
  const [productDetailPopup, setProductDetailPopup] = useState({ open: false, product: null, recommendationLine: '' });
  const pageOpenedAtRef = useRef(Date.now());
  const maxScrollDepthRef = useRef(0);
  const productImpressionObserverRef = useRef(null);
  const productImpressionSeenRef = useRef(new Set());
  const productImpressionMetaRef = useRef(new Map());

  // organization_id and product action flags from session app-data
  const [organizationId, setOrganizationId] = useState(null);
  const [segmentationAddToCart, setSegmentationAddToCart] = useState(true);
  const [segmentationProductLink, setSegmentationProductLink] = useState(true);
  const [productRecommendationSource, setProductRecommendationSource] = useState('database');
  const [productsPageUrl, setProductsPageUrl] = useState('');
  const [productsPageUrls, setProductsPageUrls] = useState([]);
  const [segmentationConfigLoaded, setSegmentationConfigLoaded] = useState(!token);
  useEffect(() => {
    if (!token) {
      setSegmentationAddToCart(true);
      setSegmentationProductLink(true);
      setProductRecommendationSource('database');
      setProductsPageUrl('');
      setProductsPageUrls([]);
      setSegmentationConfigLoaded(true);
      return;
    }
    let cancelled = false;
    setSegmentationConfigLoaded(false);
    fetchWithTimeout(`${SESSION_APP_DATA_BASE}/${encodeURIComponent(token)}/suggest-product`, { timeoutMs: 20000 })
      .then((res) => {
        if (!res.ok) throw new Error('Suggest product config unavailable');
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        const orgId = data?.organization_id;
        setOrganizationId(orgId != null && orgId !== '' ? String(orgId) : null);
      })
      .catch(() => {});
    fetchWithTimeout(`${SEGMENTATION_API_BASE}/${encodeURIComponent(token)}/segmentation`, { timeoutMs: 20000 })
      .then((res) => {
        if (!res.ok) throw new Error('Segmentation config unavailable');
        return res.json();
      })
      .then((data) => {
        if (cancelled) return;
        const productActions = resolveSegmentationProductActions(data);
        setSegmentationAddToCart(productActions.addToCart);
        setSegmentationProductLink(productActions.productLink);
        setProductRecommendationSource(
          data?.product_recommendation_source === 'products_page' ? 'products_page' : 'database'
        );
        setProductsPageUrl(String(data?.products_page_url || '').trim());
        const urls = Array.isArray(data?.products_page_urls)
          ? data.products_page_urls.map((u) => String(u || '').trim()).filter(Boolean)
          : [];
        setProductsPageUrls(urls.length ? urls : (data?.products_page_url ? [String(data.products_page_url).trim()] : []));
        setSegmentationConfigLoaded(true);
      })
      .catch(() => {
        if (!cancelled) {
          setSegmentationAddToCart(true);
          setSegmentationProductLink(true);
          setProductRecommendationSource('database');
          setProductsPageUrl('');
          setProductsPageUrls([]);
          setSegmentationConfigLoaded(true);
        }
      });
    return () => { cancelled = true; };
  }, [token]);

  // Restore cart state from navigation state when coming back from checkout
  useEffect(() => {
    if (updatedCartFromCheckout && cartItemsFromState) {
      setCartItems(cartItemsFromState);
    }
  }, [cartItemsFromState, updatedCartFromCheckout]);

  useEffect(() => {
    pageOpenedAtRef.current = Date.now();
    maxScrollDepthRef.current = 0;

    const updateScrollDepth = () => {
      if (typeof window === 'undefined' || typeof document === 'undefined') return;
      const doc = document.documentElement;
      const scrollTop = window.scrollY || doc.scrollTop || 0;
      const maxScrollable = Math.max((doc.scrollHeight || 0) - window.innerHeight, 1);
      const depth = Math.max(0, Math.min(100, Math.round((scrollTop / maxScrollable) * 100)));
      if (depth > maxScrollDepthRef.current) {
        maxScrollDepthRef.current = depth;
      }
    };

    updateScrollDepth();
    window.addEventListener('scroll', updateScrollDepth, { passive: true });
    return () => window.removeEventListener('scroll', updateScrollDepth);
  }, [sessionId]);

  const API_BASE_URL = getApiBaseUrl() || process.env.REACT_APP_API_URL || '';

  const getBehaviorMetrics = useCallback(() => ({
    screen_time_ms: Math.max(0, Date.now() - pageOpenedAtRef.current),
    scroll_depth: maxScrollDepthRef.current,
  }), []);

  const ensureProductImpressionObserver = useCallback(() => {
    if (typeof IntersectionObserver === 'undefined') return null;
    if (!productImpressionObserverRef.current) {
      productImpressionObserverRef.current = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || entry.intersectionRatio < 0.5) return;
          const productId = entry.target?.dataset?.impressionProductId;
          if (!productId || productImpressionSeenRef.current.has(productId)) return;
          const meta = productImpressionMetaRef.current.get(productId);
          if (!meta || !sessionId) return;
          productImpressionSeenRef.current.add(productId);
          saveSkinAnalyticsEvent(
            sessionId,
            {
              event_type: 'product_impression',
              event_category: 'product',
              step_name: 'card_visible',
              page_name: 'ProductRecommendationsPage',
              funnel_stage: 'recommendation',
              ...getBehaviorMetrics(),
              ...buildEnvironmentalAnalyticsFields(aqiDataFromState),
              product_id: meta.product_id,
              product_name: meta.product_name,
              product_category: meta.product_category,
              product_variant: meta.product_variant,
              product_rank: meta.product_rank,
              recommendation_reason: meta.recommendation_reason,
              event_metadata: {
                section: meta.section,
                position: meta.position,
                tab: meta.tab,
              },
            },
            token || null
          ).catch(() => {});
          productImpressionObserverRef.current?.unobserve(entry.target);
        });
      }, { threshold: [0.5] });
    }
    return productImpressionObserverRef.current;
  }, [aqiDataFromState, getBehaviorMetrics, sessionId, token]);

  const registerProductImpression = useCallback((meta) => (node) => {
    if (!node || !meta?.productId || !sessionId) return;
    if (productImpressionSeenRef.current.has(meta.productId)) return;
    const observer = ensureProductImpressionObserver();
    if (!observer) return;
    node.dataset.impressionProductId = meta.productId;
    productImpressionMetaRef.current.set(meta.productId, meta);
    observer.observe(node);
  }, [ensureProductImpressionObserver, sessionId]);

  useEffect(() => () => {
    productImpressionObserverRef.current?.disconnect();
    productImpressionObserverRef.current = null;
    productImpressionMetaRef.current.clear();
    productImpressionSeenRef.current.clear();
  }, []);

  const handleAddToCart = (product, quantity, productId) => {
    const prevQty = cartItems[productId]?.quantity || 0;
    if (prevQty === 0 && quantity > 0 && sessionId) {
      recordSkinFlowStage(
        sessionId,
        'add_to_cart',
        token || null,
        { page_name: 'ProductRecommendationsPage', quantity }
      ).catch(() => {});
    }
    setCartItems(prev => {
      if (quantity === 0) {
        // Remove item from cart if quantity is 0
        const newCart = { ...prev };
        delete newCart[productId];
        return newCart;
      }
      return {
        ...prev,
        [productId]: {
          product: product,
          quantity: quantity
        }
      };
    });
  };

  const handleCheckout = () => {
    if (isCheckoutModalOpen) return;

    // Filter cart items to only include products with quantity > 0
    const cartProducts = Object.entries(cartItems)
      .filter(([_, item]) => item.quantity > 0)
      .map(([productId, item]) => ({
        ...item,
        productId: productId // Store the productId for syncing back
      }));

    if (cartProducts.length === 0) {
      alert('Your cart is empty. Please add products to cart first.');
      return;
    }

    recordSkinFlowStage(
      sessionId,
      'checkout_started',
      token || null,
      {
        page_name: 'ProductRecommendationsPage',
        quantity: cartProducts.reduce((sum, item) => sum + Number(item.quantity || 0), 0),
      }
    ).catch(() => {});

    // Open checkout modal instead of navigating
    setIsCheckoutModalOpen(true);
  };

  const handleCloseCheckoutModal = () => {
    setIsCheckoutModalOpen(false);
  };

  const handleCartUpdate = (updatedCartItems) => {
    setCartItems(updatedCartItems);
  };

  const handleViewProduct = useCallback((product, productUrl) => {
    const numericProductId = Number(product?.id ?? product?.product_id);
    const productId = Number.isFinite(numericProductId) ? numericProductId : null;
    const baseUrl = (API_BASE_URL || '').replace(/\/$/, '');
    if (sessionId) {
      recordSkinFlowStage(sessionId, 'product_viewed', token || null, {
        page_name: 'ProductRecommendationsPage',
      }).catch(() => {});
    }
    if (baseUrl && sessionId && productId) {
      fetchWithTimeout(`${baseUrl}/api/record-viewed-product`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          skin_id: sessionId,
          session_id: sessionId,
          product_id: productId,
          ...(token && { app_session_token: token }),
        }),
        timeoutMs: 10000,
      }).catch(() => {});
    }
    if (productUrl) {
      window.open(productUrl, '_blank', 'noopener,noreferrer');
    }
  }, [API_BASE_URL, sessionId, token]);

  // Persist cart to backend whenever cart changes (Add to Cart, +/- quantity, or modal update)
  const saveCartToBackend = useCallback((items) => {
    const baseUrl = (API_BASE_URL || '').replace(/\/$/, '');
    if (!baseUrl || !sessionId) return;
    const cartProducts = Object.entries(items)
      .filter(([, item]) => item && item.quantity > 0)
      .map(([productId, item]) => ({
        id: Number(productId) || item.product?.id || item.product?.product_id,
        quantity: item.quantity,
      }));
    fetchWithTimeout(`${baseUrl}/api/save-cart-products`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        skin_id: sessionId,
        session_id: sessionId,
        ...(token && { app_session_token: token }),
        cart_products: cartProducts
      }),
      timeoutMs: 20000,
    }).then((r) => {
      if (!r.ok) console.warn('Could not save cart to backend');
    }).catch(() => { });
  }, [API_BASE_URL, sessionId, token]);

  useEffect(() => {
    saveCartToBackend(cartItems);
  }, [cartItems, saveCartToBackend]);

  // Calculate total items in cart
  const getTotalCartItems = () => {
    return Object.values(cartItems).reduce((sum, item) => sum + (item.quantity || 0), 0);
  };

  // Recommended products
  useEffect(() => {
    const fetchRecommendations = async () => {
      if (token && !segmentationConfigLoaded) return;
      if (!skinResultsFromState?.skinScore || !skinGoalDataFromState) {
        setError('Missing required data');
        setIsLoading(false);
        return;
      }

      try {
        const skinScoreForCoverage = skinResultsFromState?.skinScore || {};
        const detectionsForCoverage = skinResultsFromState?.detections || [];
        const mergedScoresForCoverage = buildRecommendationPayloadScores(
          skinScoreForCoverage,
          detectionsForCoverage
        );
        const detectedNamesForCoverage = detectionsForCoverage
          .map((d) => d.class_name || d.className)
          .filter(Boolean);
        const expectedRecommendableConditions = buildRecommendableConditions(
          detectedNamesForCoverage,
          mergedScoresForCoverage,
          detectionsForCoverage
        );

        // First, try to fetch stored products from database
        if (sessionId) {
          try {
            const storedParams = new URLSearchParams();
            if (token) storedParams.set('app_session_token', token);
            if (organizationId) storedParams.set('organization_id', organizationId);
            if (productRecommendationSource === 'products_page') storedParams.set('refresh_live', '1');
            const storedQuery = storedParams.toString() ? `?${storedParams.toString()}` : '';
            const storedResponse = await fetchWithTimeout(
              `${API_BASE_URL}/api/get-stored-products/${sessionId}${storedQuery}`,
              { timeoutMs: 20000 }
            );
            if (storedResponse.ok) {
              const storedData = await storedResponse.json().catch(() => ({}));
              // Normalize legacy shape ({morning:[], evening:[]}) and new flat list [] into a flat list
              const flat = dedupeProductsById(flattenRecommendedProducts(storedData.recommended_products));
              const hasStoredProducts = Array.isArray(flat) && flat.length > 0;
              // products_page: always re-fetch live catalog (stored IDs are analytics-only snapshots)
              if (
                storedData.success
                && hasStoredProducts
                && productRecommendationSource !== 'products_page'
              ) {
                const shuffleArray = (array) => {
                  const shuffled = [...array];
                  for (let i = shuffled.length - 1; i > 0; i--) {
                    const j = Math.floor(Math.random() * (i + 1));
                    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
                  }
                  return shuffled;
                };
                const shuffled = shuffleArray(flat);
                recordSkinFlowStage(
                  sessionId,
                  'recommendations_viewed',
                  token || null,
                  {
                    page_name: 'ProductRecommendationsPage',
                    source: 'stored',
                    total_count: shuffled.length,
                  }
                ).catch(() => {});
                setRecommendations({ ...storedData, recommended_products: shuffled });
                try {
                  const current = getSkinFlowState(sessionId) || {};
                  const merged = { ...current, recommended_products: shuffled, sessionId };
                  saveSkinFlowState(sessionId, merged);
                } catch (e) {}
                setIsLoading(false);
                return;
              }
              if (productRecommendationSource === 'products_page' && hasStoredProducts) {
                console.info(
                  '[ProductRecommendations] products_page: regenerating from live catalog',
                  { storedCount: flat.length, expectedConditions: expectedRecommendableConditions.length }
                );
              }
            }
          } catch (storedErr) {
            // If fetching stored products fails, continue to regenerate
            console.log('Could not fetch stored products, regenerating...', storedErr);
          }
        }

        // Regenerate products only if stored products not found
        const skinScore = skinResultsFromState.skinScore;
        const detections = skinResultsFromState.detections || [];
        const mergedConditionScores = buildRecommendationPayloadScores(skinScore, detections);
        const detectedFromDetections = detections.map(d => d.class_name || d.className).filter(Boolean);
        const detectedConditions = buildRecommendableConditions(detectedFromDetections, mergedConditionScores, detections);

        const userData = userDataFromState || {};
        const userSkinType = userData.skin_type != null && userData.skin_type !== '' ? userData.skin_type : null;
        const userAge = parseOptionalAge(userData.age ?? userData.Age ?? null);
        console.log('[ProductRecommendations] recommend-products payload:', {
          user_info: { skin_type: userSkinType, age: userAge, userData_keys: Object.keys(userData || {}) },
          detected_conditions: detectedConditions,
          session_id: sessionId,
          organization_id: organizationId || undefined
        });

        const body = {
          session_id: sessionId,
          final_skin_score: skinScore.final_skin_score || skinScore.score || 0,
          skin_grade: skinScore.grade || 'Fair',
          condition_scores: mergedConditionScores,
          detected_conditions: detectedConditions,
          detections,
          skin_label_scores: skinScore.skin_label_scores || {},
          attribute_scores: skinScore.attribute_scores || {},
          app_session_token: token,
          product_recommendation_source: productRecommendationSource,
          ...(productRecommendationSource === 'products_page' && (productsPageUrls.length || productsPageUrl)
            ? {
                products_page_url: productsPageUrls[0] || productsPageUrl,
                products_page_urls: productsPageUrls.length ? productsPageUrls : [productsPageUrl],
              }
            : {}),
          ...(organizationId ? { organization_id: organizationId } : {}),
          ...(userSkinType != null ? { user_skin_type: userSkinType } : {}),
          ...(userAge != null ? { user_age: userAge } : {})
        };
        const response = await fetchWithTimeout(`${API_BASE_URL}/api/recommend-products`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
          timeoutMs: 30000,
        });

        if (!response.ok) {
          throw new Error('Failed to fetch recommendations');
        }

        const data = await response.json();
        console.log('[ProductRecommendations] recommend-products response:', {
          success: data.success,
          total_products_recommended: Array.isArray(data.recommended_products) ? data.recommended_products.length : (data.total_products_recommended || 0)
        });
        if (data.success) {
          // Normalize to flat list and shuffle
          const flat = dedupeProductsById(flattenRecommendedProducts(data.recommended_products));
          const shuffleArray = (array) => {
            const shuffled = [...array];
            for (let i = shuffled.length - 1; i > 0; i--) {
              const j = Math.floor(Math.random() * (i + 1));
              [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
            }
            return shuffled;
          };
          const shuffled = shuffleArray(flat);
          recordSkinFlowStage(
            sessionId,
            'recommendations_viewed',
            token || null,
            {
              page_name: 'ProductRecommendationsPage',
              source: 'generated',
              total_count: shuffled.length,
            }
          ).catch(() => {});

          setRecommendations({ ...data, recommended_products: shuffled });
          try {
            const current = getSkinFlowState(sessionId) || {};
            const merged = { ...current, recommended_products: shuffled, sessionId };
            saveSkinFlowState(sessionId, merged);
          } catch (e) {}
        } else {
          throw new Error(data.error || 'Failed to get recommendations');
        }
      } catch (err) {
        setError(err.message || 'Failed to load product recommendations');
      } finally {
        setIsLoading(false);
      }
    };

    fetchRecommendations();
  }, [
    skinResultsFromState,
    skinGoalDataFromState,
    userDataFromState,
    API_BASE_URL,
    sessionId,
    token,
    organizationId,
    aqiDataFromState,
    getBehaviorMetrics,
    productRecommendationSource,
    productsPageUrl,
    productsPageUrls,
    segmentationConfigLoaded,
  ]);


  const handleCloseUspPopup = () => {
    setUspPopup({ open: false, text: '', heading: '', position: { top: 0, left: 0 } });
  };

  const handleCloseProductDetailPopup = () => {
    setProductDetailPopup({ open: false, product: null, recommendationLine: '' });
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (uspPopup.open && !e.target.closest('.product-usp-popup') && !e.target.closest('.product-usp')) {
        handleCloseUspPopup();
      }
    };

    if (uspPopup.open) {
      document.addEventListener('click', handleClickOutside);
      return () => {
        document.removeEventListener('click', handleClickOutside);
      };
    }
  }, [uspPopup.open]);

  const handleBack = () => {
    navigate(pathWithSessionId('/skin-routine', sessionId), {
      state: {
        capturedImage: capturedImageFromState,
        userData: userDataFromState,
        sessionId: sessionId,
        skinResults: skinResultsFromState,
        skinGoalData: skinGoalDataFromState,
        routineData: routineDataFromState,
        aqiData: aqiDataFromState,
        ...(token && { token })
      }
    });
  };

  const handleHome = () => {
    navigate(sessionLandingPath);
  };



  return (
    <div className="product-recommendations-page-shell page-bg">
      <div className="product-recommendations-page-nav">
        <button
          type="button"
          className="product-recommendations-page-back-button"
          onClick={handleBack}
          aria-label="Go back"
        >
          ←
        </button>
        <div className="product-recommendations-page-title-wrapper">
          <h3 className="product-recommendations-page-title">
            Recommended Products
          </h3>
          <button
            type="button"
            className="product-recommendations-page-home-button"
            onClick={handleHome}
            aria-label="Go to home"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
              <polyline points="9 22 9 12 15 12 15 22" />
            </svg>
          </button>
          {segmentationAddToCart && getTotalCartItems() > 0 && (
            <button
              type="button"
              className="product-recommendations-page-checkout-button-icon"
              onClick={handleCheckout}
              title={`Checkout (${getTotalCartItems()} items)`}
              aria-label={`Checkout with ${getTotalCartItems()} items`}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="21" r="1"></circle>
                <circle cx="20" cy="21" r="1"></circle>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
              </svg>
              {getTotalCartItems() > 0 && (
                <span className="product-recommendations-page-checkout-badge-icon">
                  {getTotalCartItems()}
                </span>
              )}
            </button>
          )}
        </div>
      </div>

      <main className="product-recommendations-page-main">
        <div className="product-recommendations-page-container">
          <section className="product-recommendations-page-layout">

            {isLoading ? (
              <div className="product-recommendations-loading">
                <div className="loading-spinner"></div>
                <p>Loading personalized recommendations...</p>
              </div>
            ) : error ? (
              <div className="product-recommendations-error">
                <p>{error}</p>
                <button onClick={() => window.location.reload()}>Retry</button>
              </div>
            ) : recommendations ? (
              <>
                {(() => {
                  const productsFlat = flattenRecommendedProducts(recommendations.recommended_products);
                  return (
                <div className="product-recommendations-content">
                  <div className="recommended-live-product-grid">
                    {productsFlat && productsFlat.length > 0 ? (
                      productsFlat.map((product, idx) => {
                        const productId = product.product_id || product.id || `prod_${(product.product_name || product.name || '').replace(/\s+/g, '_')}_${idx}`;
                        const cartQuantity = cartItems[productId]?.quantity || 0;
                        return (
                          <RecommendedProductCard
                            key={productId}
                            product={product}
                            productId={productId}
                            cartQuantity={cartQuantity}
                            showAddToCart={segmentationAddToCart}
                            showProductLink={segmentationProductLink}
                            onAddToCart={handleAddToCart}
                            onViewProduct={handleViewProduct}
                            storeUrl={productsPageUrl}
                            cardRef={registerProductImpression({
                              productId,
                              product_name: product.product_name || product.name,
                              product_category: product.category || product.product_category || null,
                              product_variant: product.variant || product.product_variant || null,
                              product_rank: idx + 1,
                              recommendation_reason: product.suggested_for_wording || '',
                              section: 'recommended',
                              position: idx + 1,
                            })}
                          />
                        );
                      })
                    ) : (
                      <div className="no-products-message">
                        <p>No products found.</p>
                      </div>
                    )}
                  </div>
                </div>
                  );
                })()}
              </>
            ) : null}
          </section>
        </div>
      </main>

      {/* USP Popup */}
      {uspPopup.open && (
        <div
          className="product-usp-popup-overlay"
          onClick={handleCloseUspPopup}
        >
          <div
            className="product-usp-popup"
            style={{
              top: `${uspPopup.position.top}px`,
              left: `${uspPopup.position.left}px`,
              width: `${Math.max(uspPopup.position.width, 300)}px`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="product-usp-popup-content">
              <button
                className="product-usp-popup-close"
                onClick={handleCloseUspPopup}
                aria-label="Close"
              >
                ×
              </button>
              {uspPopup.heading && <h3 className="product-usp-popup-heading">{uspPopup.heading}</h3>}
              {(() => {
                const raw = String(uspPopup.text);
                const isHtml = /<[a-z][\s\S]*>/i.test(raw);
                const html = isHtml ? sanitizeHtml(raw) : sanitizeHtml(raw.replace(/\r\n/g, '\n').replace(/\r/g, '\n').replace(/\n/g, '<br />'));
                return <div className="product-usp-popup-text" dangerouslySetInnerHTML={{ __html: html }} />;
              })()}
            </div>
          </div>
        </div>
      )}

      {productDetailPopup.open && productDetailPopup.product && (() => {
        const product = productDetailPopup.product;
        const productTypeDisplay = product.product_type || product.productType || (Array.isArray(product.product_types) && product.product_types?.[0]) || product.product_types;
        const majorUspText = product.major_usp ? stripHtml(product.major_usp) : '';
        const productId = product.product_id || product.id || product.sku || `prod_${product.product_name || 'product'}`;
        const cartQuantity = cartItems[productId]?.quantity || 0;
        const modalLink = product.product_link || '';
        const showModalLinkButton = segmentationProductLink && isValidProductUrl(modalLink);
        return (
          <div className="product-usp-popup-overlay" onClick={handleCloseProductDetailPopup}>
            <div className="product-usp-popup-modal" onClick={e => e.stopPropagation()}>
              <button className="product-usp-popup-close" onClick={handleCloseProductDetailPopup} aria-label="Close">×</button>
              <div className="product-usp-popup-content">
                <div className="product-card skin-routine-product-modal-card">
                  <div className="product-card-image">
                    {product.image_url && <img src={product.image_url} alt={product.product_name || 'Product'} onError={(e) => { e.target.style.display = 'none'; }} />}
                  </div>
                  <div className="product-info">
                    <div className="product-name-header">
                      {product.product_link ? (
                        <a className="product-name product-name-link" href={product.product_link} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                          {product.product_name || 'Product'}
                        </a>
                      ) : (
                        <span className="product-name">{product.product_name || 'Product'}</span>
                      )}
                    </div>
                    {productTypeDisplay && (
                      <div className="product-type-heading">Type: {productTypeDisplay}</div>
                    )}
                    {majorUspText && (
                      <div className="product-major-usp-wrapper">
                        <span className="product-card-usp-heading">Key benefit</span>
                        <span className="product-major-usp-text">{majorUspText}</span>
                      </div>
                    )}
                    {Array.isArray(product.concerns_treated) && product.concerns_treated.length > 0 && (
                      <div className="product-info-section">
                        <div className="product-info-label"><img src={`${process.env.PUBLIC_URL}/icon/treat.png`} alt="Treats" className="product-info-icon" /><span>Treats</span></div>
                        <div className="product-tags-container">{product.concerns_treated.map((concern, i) => <span key={`modal-concern-${i}`} className="product-tag">{concern}</span>)}</div>
                      </div>
                    )}
                  </div>
                  <div className="product-card-footer">
                    <div className="product-price-quantity">
                      <span className="product-price">{formatProductPrice(product.price ?? product.sale_price ?? product.amount ?? product.mrp)}</span>
                    </div>
                    {segmentationAddToCart ? (
                      <ProductCartCounter
                        key={`popup-counter-${productId}`}
                        product={product}
                        onAddToCart={(nextProduct, quantity) => handleAddToCart(nextProduct, quantity, productId)}
                        initialQuantity={cartQuantity}
                      />
                    ) : null}
                    {showModalLinkButton ? (
                      <button type="button" className="skin-action-button product-link-button" onClick={(e) => { e.stopPropagation(); handleViewProduct(product, modalLink); }} title="Open product page">
                        View product
                      </button>
                    ) : null}
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })()}

      <PageFooter />

      {/* Checkout Modal */}
      <CheckoutModal
        isOpen={isCheckoutModalOpen}
        onClose={handleCloseCheckoutModal}
        cartItems={Object.entries(cartItems)
          .filter(([_, item]) => item.quantity > 0)
          .map(([productId, item]) => ({
            ...item,
            productId: productId
          }))}
        onCartUpdate={handleCartUpdate}
        sessionId={sessionId}
        apiBaseUrl={API_BASE_URL}
        token={token}
        customerDetails={{
          name: userDataFromState?.name || userDataFromState?.Name || '',
          phone: userDataFromState?.mobileNumber || userDataFromState?.mobile_number || '',
          email: userDataFromState?.email || userDataFromState?.Email || '',
        }}
      />
    </div>
  );
};

export default ProductRecommendationsPage;

